import { PublicLayout } from "@/features/navigation/components/public-layout.component";
import { getNameInitials } from "@/libs/common/string-utils";
import { getPublicDesignerBySlug } from "@/services/user/designer.server";
import { PortfolioReferenceCard } from "@/ui";
import { getPortfolioImage, getProfileImage } from "@/utils/images.server";
import { getDesignerFullName } from "@/utils/user.server";
import {
	Avatar,
	Box,
	CloseButton,
	Container,
	Flex,
	Group,
	MediaQuery,
	Paper,
	Stack,
	Text,
} from "@mantine/core";
import type { LoaderArgs } from "@remix-run/node";
import { json } from "@remix-run/node";
import { Link, Outlet, useLoaderData, useParams } from "@remix-run/react";
import format from "date-fns/format";
import invariant from "tiny-invariant";

export default function OurDesignersDetail() {
	const loaderData = useLoaderData<typeof loader>();
	const params = useParams<"slug">();

	return (
		<PublicLayout>
			<Container size="xl">
				<Box
					px={{
						base: "xl",
						md: 56,
						lg: 0,
					}}
					mx="auto"
					mt={{ base: 48, lg: 64 }}
				>
					<Paper
						component={Flex}
						withBorder
						direction={{
							base: "column",
							md: "row",
						}}
						sx={{ flex: 1, overflow: "hidden" }}
					>
						<Box
							w={{ base: "100%", md: "50%" }}
							p="xl"
							sx={{ overflowY: "auto" }}
						>
							<Group position="apart">
								<Group spacing="xl">
									<Avatar src={loaderData.avatar || undefined} w={144} h={144}>
										{!loaderData.avatar && getNameInitials(loaderData.fullName)}
									</Avatar>
									<div>
										<div>
											<Text weight={600}>{loaderData.fullName}</Text>
											<Text size="sm">{loaderData.workTitle}</Text>
											<Text size="xs" color="dimmed">
												Member since: {loaderData.joinedAt}
											</Text>
										</div>
									</div>
								</Group>
								<MediaQuery smallerThan="lg" styles={{ display: "none" }}>
									<CloseButton component={Link} to="/our-designers" />
								</MediaQuery>
							</Group>

							<Text
								size="sm"
								mt="xl"
								sx={{
									whiteSpace: "pre-wrap",
									overflowWrap: "break-word",
								}}
							>
								{loaderData.bio}
							</Text>
						</Box>

						{/* right side with portfolio references */}
						<Box
							w={{
								base: "100%",
								md: "50%",
							}}
							p="xl"
							sx={{
								overflowY: "auto",
							}}
						>
							<Group position="apart" mb="xl">
								<Text size="sm" weight={600} display="block" color="dimmed">
									References ({loaderData.portfolio.length})
								</Text>
								<MediaQuery smallerThan="md" styles={{ display: "none" }}>
									<CloseButton component={Link} to="/our-designers" />
								</MediaQuery>
							</Group>

							{loaderData.portfolio.length > 0 ? (
								<Stack spacing="xl">
									{loaderData.portfolio.map((portfolio) => (
										// eslint-disable-next-line react/jsx-key
										<PortfolioReferenceCard
											key={portfolio.id}
											image={portfolio.image}
											title={portfolio.title}
											description={portfolio.description}
											href={`/our-designers/${params.slug}/portfolio/${portfolio.id}`}
										/>
									))}
								</Stack>
							) : (
								<Text size="sm">(No references to show)</Text>
							)}
						</Box>
					</Paper>
				</Box>
				<Outlet />
				{/* <Footer /> */}
			</Container>
		</PublicLayout>
	);
}

export async function loader({ params }: LoaderArgs) {
	invariant(params.slug, "Slug is required");

	const designer = await getPublicDesignerBySlug(params.slug, {
		select: {
			id: true,
			profilePicture: true,
			firstName: true,
			lastName: true,
			bio: true,
			portfolio: true,
			workTitle: true,
			createdAt: true,
		},
	});

	return json({
		id: designer.id,
		avatar: getProfileImage(designer.profilePicture),
		fullName: getDesignerFullName(designer)!,
		workTitle: designer.workTitle,
		joinedAt: format(new Date(designer.createdAt), "dd.MM.y"),
		bio: designer.bio,
		portfolio: designer.portfolio.map((portfolio) => ({
			...portfolio,
			image: getPortfolioImage(portfolio.image),
		})),
	});
}
